import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Save, Plus, Trash2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_SECTIONS = [
  {
    section: "Site Banner",
    description: "The dismissible strip at the top of the site.",
    fields: [
      { key: "banner_enabled", label: "Show banner?", type: "toggle", placeholder: "true" },
      { key: "banner_text", label: "Banner text", type: "text", placeholder: "Site One — Opens 2027 Season." },
      { key: "banner_cta_label", label: "CTA label", type: "text", placeholder: "Join the waitlist" },
      { key: "banner_cta_link", label: "CTA link", type: "text", placeholder: "/waitlist" },
    ],
  },
  {
    section: "Home — Hero",
    fields: [
      { key: "hero_tagline", label: "Hero Tagline", type: "text", placeholder: "Your private piece of the Bay of Fundy." },
      { key: "hero_subtitle", label: "Hero Subtitle", type: "textarea", placeholder: "Exclusive whole-property rental…" },
    ],
  },
  {
    section: "Home — Experience",
    fields: [
      { key: "experience_heading", label: "Experience Heading", type: "text", placeholder: "Where the forest meets the Bay..." },
    ],
  },
  {
    section: "Origin (Story)",
    fields: [
      { key: "story_heading", label: "Story Heading", type: "text", placeholder: "How we found this place." },
    ],
  },
  {
    section: "Wilderness (Land)",
    fields: [
      { key: "land_heading", label: "Land Heading", type: "text", placeholder: "Two sites on the Bay of Fundy." },
      { key: "land_subtitle", label: "Land Subtitle", type: "textarea", placeholder: "Private, off-grid…" },
      { key: "property_heading", label: "Property / Features Heading", type: "text", placeholder: "" },
      { key: "roadin_heading", label: "Road In Heading", type: "text", placeholder: "" },
      { key: "trails_heading", label: "Trails Heading", type: "text", placeholder: "" },
    ],
  },
  {
    section: "Off Grid",
    fields: [
      { key: "offgrid_heading", label: "Off Grid Heading", type: "text", placeholder: "Living off the grid." },
      { key: "offgrid_subtitle", label: "Off Grid Subtitle", type: "textarea", placeholder: "" },
      { key: "offgrid_water", label: "Water & Waste card", type: "textarea", placeholder: "" },
      { key: "offgrid_power", label: "Power card", type: "textarea", placeholder: "" },
      { key: "offgrid_pets", label: "Dogs & Pets card", type: "textarea", placeholder: "" },
    ],
  },
  {
    section: "Tides / Environment",
    fields: [
      { key: "tide_heading", label: "Tide Heading", type: "text", placeholder: "" },
      { key: "tide_description", label: "Tide Description", type: "textarea", placeholder: "The Bay of Fundy has the highest tides on Earth…" },
    ],
  },
  {
    section: "Activities",
    fields: [
      { key: "attractions_heading", label: "Attractions Heading", type: "text", placeholder: "A local's recommendations." },
      { key: "attractions_subtitle", label: "Attractions Subtitle", type: "textarea", placeholder: "" },
      { key: "services_heading", label: "Services Heading", type: "text", placeholder: "" },
    ],
  },
  {
    section: "Photography / Guest Book / FAQ",
    fields: [
      { key: "gallery_heading", label: "Gallery Heading", type: "text", placeholder: "" },
      { key: "gallery_subtitle", label: "Gallery Subtitle", type: "text", placeholder: "" },
      { key: "guestbook_heading", label: "Guest Book Heading", type: "text", placeholder: "" },
      { key: "faq_heading", label: "FAQ Heading", type: "text", placeholder: "Common questions." },
    ],
  },
  {
    section: "Footer",
    fields: [
      { key: "footer_heading", label: "Footer Heading", type: "text", placeholder: "" },
      { key: "footer_description", label: "Footer Description", type: "textarea", placeholder: "" },
      { key: "contact_email", label: "Contact Email", type: "text", placeholder: "whalecoveoffgrid@gmail.com" },
    ],
  },
];

function ContentEditorTab({ authHeader }) {
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [query, setQuery] = useState("");

  const fetchContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/content`, authHeader);
      const map = {};
      res.data.content.forEach((item) => { map[item.key] = item.value; });
      setContent(map);
      setDirty(false);
    } catch (e) {
      console.error(e);
    }
  }, [authHeader]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const update = (key, value) => {
    setContent((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, content, authHeader);
      setSaved(true);
      setDirty(false);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const lowerQ = query.trim().toLowerCase();
  const visibleSections = CONTENT_SECTIONS.map((s) => {
    if (!lowerQ) return s;
    const fields = s.fields.filter(
      (f) => f.key.toLowerCase().includes(lowerQ) || f.label.toLowerCase().includes(lowerQ),
    );
    return fields.length ? { ...s, fields } : null;
  }).filter(Boolean);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="content-editor">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h3 className="font-display text-wc-forest text-lg font-semibold">Edit Site Content</h3>
        <div className="flex items-center gap-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by key or label…"
            className="h-9 w-56 text-sm"
            data-testid="content-filter-input"
          />
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-wc-teal text-white hover:bg-wc-forest font-body text-sm gap-1.5 disabled:opacity-50"
            data-testid="content-save-button"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : saved ? "Saved!" : dirty ? "Save Changes" : "No changes"}
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        {visibleSections.map((section) => (
          <div key={section.section} className="border-l-2 border-wc-teal/30 pl-4" data-testid={`content-section-${section.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="mb-3">
              <h4 className="font-display text-wc-forest text-sm font-semibold">{section.section}</h4>
              {section.description && (
                <p className="font-body text-gray-400 text-xs mt-0.5">{section.description}</p>
              )}
            </div>
            <div className="space-y-3">
              {section.fields.map((field) => (
                <div key={field.key}>
                  <Label className="font-body text-xs text-gray-600 flex items-center gap-2">
                    {field.label}
                    <code className="font-mono text-[10px] text-gray-300">{field.key}</code>
                  </Label>
                  {field.type === "toggle" ? (
                    <div className="mt-1 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => update(field.key, content[field.key] === "true" ? "false" : "true")}
                        className={`inline-flex items-center rounded-full px-4 py-1.5 text-xs font-body transition-colors ${
                          content[field.key] === "true"
                            ? "bg-wc-teal text-white"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                        data-testid={`content-toggle-${field.key}`}
                      >
                        {content[field.key] === "true" ? "Enabled" : "Disabled"}
                      </button>
                    </div>
                  ) : field.type === "textarea" ? (
                    <textarea
                      value={content[field.key] || ""}
                      onChange={(e) => update(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal"
                    />
                  ) : (
                    <Input
                      value={content[field.key] || ""}
                      onChange={(e) => update(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="mt-1"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {dirty && (
        <div className="mt-6 bg-wc-cream/60 border border-wc-teal/30 rounded-md px-4 py-3 text-wc-forest text-sm font-body flex items-center justify-between sticky bottom-4">
          <span>You have unsaved changes.</span>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-wc-teal text-white hover:bg-wc-forest font-body text-sm gap-1.5"
            data-testid="content-save-sticky"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  );
}

function SettingsTab({ authHeader }) {
  const [pricing, setPricing] = useState({ nightly_rate: "", minimum_nights: 2, maximum_group_size: 12, currency: "CAD" });
  const [blocked, setBlocked] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [fireBan, setFireBan] = useState({ status: "default", note: "" });
  const [fireSaving, setFireSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/availability`, authHeader);
      if (res.data.pricing) setPricing(res.data.pricing);
      setBlocked(res.data.blocked_dates || []);
      const fbRes = await axios.get(`${API}/admin/fire-ban`, authHeader);
      if (fbRes.data.fire_ban) setFireBan(fbRes.data.fire_ban);
    } catch (e) {
      console.error(e);
    }
  }, [authHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const savePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pricing`, pricing, authHeader);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async () => {
    if (!dateRange.from || !dateRange.to) return;
    await axios.post(`${API}/admin/availability/block`, {
      start_date: dateRange.from.toISOString().split("T")[0],
      end_date: dateRange.to.toISOString().split("T")[0],
      reason,
    }, authHeader);
    setDateRange({ from: undefined, to: undefined });
    setReason("");
    fetchData();
  };

  const removeBlock = async (id) => {
    await axios.delete(`${API}/admin/availability/block/${id}`, authHeader);
    fetchData();
  };

  return (
    <div className="space-y-6" data-testid="settings-panel">
      {/* Pricing */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4">Pricing</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <Label className="font-body text-sm text-gray-600">Nightly Rate</Label>
            <Input value={pricing.nightly_rate} onChange={(e) => setPricing({ ...pricing, nightly_rate: e.target.value })}
              placeholder="e.g. 150" className="mt-1" data-testid="pricing-rate-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-gray-600">Min Nights</Label>
            <Input type="number" value={pricing.minimum_nights} onChange={(e) => setPricing({ ...pricing, minimum_nights: parseInt(e.target.value) || 2 })}
              className="mt-1" data-testid="pricing-min-nights-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-gray-600">Max Group Size</Label>
            <Input type="number" value={pricing.maximum_group_size} onChange={(e) => setPricing({ ...pricing, maximum_group_size: parseInt(e.target.value) || 12 })}
              className="mt-1" data-testid="pricing-max-group-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-gray-600">Currency</Label>
            <Input value={pricing.currency} onChange={(e) => setPricing({ ...pricing, currency: e.target.value })}
              className="mt-1" />
          </div>
        </div>
        <Button onClick={savePricing} disabled={saving}
          className="bg-wc-teal text-white hover:bg-wc-forest font-body text-sm gap-1.5"
          data-testid="pricing-save-button">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Pricing"}
        </Button>
      </div>

      {/* Blocked dates */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4">Blocked Dates</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Calendar
              mode="range" selected={dateRange} onSelect={setDateRange}
              numberOfMonths={1} disabled={{ before: new Date() }}
              classNames={{
                day_selected: "bg-red-500 text-white hover:bg-red-500",
                day_range_middle: "aria-selected:bg-red-100 aria-selected:text-red-700",
              }}
            />
            <div className="mt-3 flex gap-2">
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (optional)" className="flex-1 text-sm" />
              <Button onClick={addBlock} disabled={!dateRange.from || !dateRange.to}
                className="bg-red-500 text-white hover:bg-red-600 text-sm gap-1"
                data-testid="block-dates-button">
                <Plus className="w-4 h-4" /> Block
              </Button>
            </div>
          </div>
          <div>
            <p className="font-body text-sm text-gray-500 mb-3">Currently blocked:</p>
            {blocked.length === 0 ? (
              <p className="font-body text-gray-300 text-sm">No dates blocked.</p>
            ) : (
              <div className="space-y-2">
                {blocked.map((b) => (
                  <div key={b.id} className="flex items-center justify-between bg-red-50 border border-red-100 rounded px-3 py-2">
                    <div>
                      <span className="font-body text-sm text-red-700">{b.start_date} — {b.end_date}</span>
                      {b.reason && <span className="font-body text-xs text-red-400 ml-2">({b.reason})</span>}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeBlock(b.id)}
                      className="text-red-400 hover:bg-red-100 h-7 w-7 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fire Ban */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4">Fire Ban Override</h3>
        <p className="font-body text-gray-500 text-xs mb-4">Override the automatic seasonal fire status shown on the Tonight dashboard. "Default" uses automatic seasonal rules.</p>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label className="font-body text-sm text-gray-600">Status</Label>
            <select
              value={fireBan.status}
              onChange={(e) => setFireBan({ ...fireBan, status: e.target.value })}
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 font-body text-sm"
              data-testid="fire-ban-select"
            >
              <option value="default">Default (automatic)</option>
              <option value="full_ban">Full Ban — no campfires</option>
              <option value="no_restrictions">No Restrictions</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="font-body text-sm text-gray-600">Note (shown to guests)</Label>
            <Input value={fireBan.note} onChange={(e) => setFireBan({ ...fireBan, note: e.target.value })}
              placeholder="e.g. Extreme fire risk this week" className="mt-1" data-testid="fire-ban-note" />
          </div>
          <Button onClick={async () => {
              setFireSaving(true);
              try { await axios.put(`${API}/admin/fire-ban`, fireBan, authHeader); } catch (e) { console.error(e); }
              finally { setFireSaving(false); }
            }} disabled={fireSaving}
            className="bg-wc-teal text-white hover:bg-wc-forest font-body text-sm gap-1.5"
            data-testid="fire-ban-save"
          >
            <Save className="w-4 h-4" /> {fireSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ContentEditor({ authHeader, settingsMode }) {
  if (settingsMode) return <SettingsTab authHeader={authHeader} />;
  return <ContentEditorTab authHeader={authHeader} />;
}
